/**
 * Data layer for `link-management-crud-permission-scope`. Thin typed fetch wrappers over /api/v1/link-management-crud-permission-scope.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface LinkManagementCrudPermissionScopeRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListLinkManagementCrudPermissionScopeResponse {
	count: number;
	items: LinkManagementCrudPermissionScopeRow[];
}

export interface CreateLinkManagementCrudPermissionScopeBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/link-management-crud-permission-scope";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListLinkManagementCrudPermissionScopeResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListLinkManagementCrudPermissionScopeResponse>(res);
}

export async function create(body: CreateLinkManagementCrudPermissionScopeBody): Promise<LinkManagementCrudPermissionScopeRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<LinkManagementCrudPermissionScopeRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const linkManagementCrudPermissionScopeQueryKeys = {
	all: ["link-management-crud-permission-scope"] as const,
	list: () => [...linkManagementCrudPermissionScopeQueryKeys.all] as const,
	detail: (id: string) => [...linkManagementCrudPermissionScopeQueryKeys.all, id] as const,
};
